import React, {
  useState,
  memo,
  useCallback,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  Card,
  CardContent,
  TextField,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import { Lock as LockIcon } from "@mui/icons-material";
import { Comment as CommentIcon } from "@mui/icons-material";
import {
  NoteNodeData,
  NodeEditingState,
  EmojiReaction,
  ResizableNoteNodeRef,
} from "../types";
import CommentPopover from "./CommentPopover";

const ResizableNoteNode = forwardRef<ResizableNoteNodeRef, any>(
  (
    {
      data,
      id,
      selected,
      onCursorMove,
      onTextChange,
      onTextChangeRealtime,
      onEditStart,
      onEditEnd,
      onDimensionChange,
      onEmojiToggle,
      isBeingEditedByOther = false,
      editingUser,
      currentUserId = "unknown",
      isSelected = false,
      reactFlow,
      // Comment handlers passed from parent
      onAddComment,
      onAddCommentReaction,
      onRemoveCommentReaction,
      currentUserName,
      currentUserColor,
      // Add hover event props
      onMouseEnter,
      onMouseLeave,
    },
    ref
  ) => {
    const { Handle, Position, NodeProps, NodeResizer } = reactFlow;
    const [isEditingText, setIsEditingText] = useState(false);
    const [noteText, setNoteText] = useState(data.text || "");
    const [isHovered, setIsHovered] = useState(false);
    // Add state for CommentPopover within ResizableNoteNode
    const [commentPopoverState, setCommentPopoverState] = useState<{
      anchorEl: HTMLElement | null;
    }>({
      anchorEl: null,
    });

    // Track current dimensions
    const [currentDimensions, setCurrentDimensions] = useState({
      width: data.width || 110,
      height: data.height || 110,
    });

    const textFieldRef = useRef<HTMLInputElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const isEditingRef = useRef(false);
    const lastSavedTextRef = useRef(data.text || "");
    const lastExternalUpdateRef = useRef(data.text || "");
    const textChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Handle comment indicator click locally
    const handleCommentIndicatorClick = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault(); // Add this line
        setCommentPopoverState({
          anchorEl: event.currentTarget as HTMLElement,
        });
      },
      []
    );

    // Handle comment popover close locally
    const handleCommentPopoverClose = useCallback(() => {
      setCommentPopoverState({
        anchorEl: null,
      });
    }, []);

    // Update local state when data changes (from other users)
    useEffect(() => {
      if (!isEditingText && data.text !== lastSavedTextRef.current) {
        setNoteText(data.text || "");
        lastSavedTextRef.current = data.text || "";
        lastExternalUpdateRef.current = data.text || "";
      }
    }, [data.text, isEditingText]);

    // Update dimensions when data changes (from other users)
    useEffect(() => {
      const newWidth = data.width || 110;
      const newHeight = data.height || 110;

      if (
        currentDimensions.width !== newWidth ||
        currentDimensions.height !== newHeight
      ) {
        setCurrentDimensions({ width: newWidth, height: newHeight });
      }
    }, [data.width, data.height, currentDimensions]);

    // Keep track of editing state in ref
    useEffect(() => {
      isEditingRef.current = isEditingText;
    }, [isEditingText]);

    // Handle clicks outside to save changes - FIXED
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Element;
        if (!isEditingRef.current) return;

        if (
          target.closest(".react-flow__handle") ||
          target.closest(".react-flow__controls") ||
          target.closest(".react-flow__minimap") ||
          target.closest('[role="dialog"]') ||
          target.closest(".node-toolbar") ||
          target.closest(".MuiPopover-root") || // Add this line
          target.closest('[role="presentation"]') // Add this line for MUI Popover
        ) {
          return;
        }

        if (
          cardRef.current &&
          !cardRef.current.contains(target as Node) &&
          isEditingRef.current
        ) {
          setTimeout(() => {
            if (isEditingRef.current) {
              handleSave();
            }
          }, 50);
        }
      };

      document.addEventListener("mousedown", handleClickOutside, true);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside, true);
      };
    }, []);

    // Handle resize events from NodeResizer
    const handleResize = useCallback(
      (event: any, params: { width: number; height: number }) => {
        const { width, height } = params;
        setCurrentDimensions({ width, height });
        onDimensionChange?.(id, width, height);
      },
      [id, onDimensionChange]
    );

    const handleResizeStart = useCallback(() => {
      onEditStart?.(id, "general");
    }, [id, onEditStart]);

    const handleResizeEnd = useCallback(() => {
      onEditEnd?.(id);
    }, [id, onEditEnd]);

    const handleSave = useCallback(() => {
      if (!isEditingRef.current) return;

      setIsEditingText(false);
      lastSavedTextRef.current = noteText;
      onTextChange?.(id, noteText);
      onEditEnd?.(id);
    }, [id, noteText, onTextChange, onEditEnd]);

    useImperativeHandle(
      ref,
      () => ({
        handleSave,
        isEditing: isEditingText,
      }),
      [handleSave, isEditingText]
    );

    const handleStartEditingText = useCallback(
      (e: React.MouseEvent) => {
        if (isBeingEditedByOther) return;

        e.stopPropagation();
        e.preventDefault();

        setIsEditingText(true);
        onEditStart?.(id, "text");

        setTimeout(() => {
          if (textFieldRef.current) {
            textFieldRef.current.focus();
            textFieldRef.current.select();
          }
        }, 100);
      },
      [isBeingEditedByOther, onEditStart, id]
    );

    const handleKeyPress = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && e.ctrlKey) {
          handleSave();
        } else if (e.key === "Escape") {
          setNoteText(lastSavedTextRef.current);
          setIsEditingText(false);
          onEditEnd?.(id);
        }
        e.stopPropagation();
      },
      [handleSave, onEditEnd, id]
    );

    const handleTextFieldClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
    }, []);

    const handleTextFieldMouseDown = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    const handleMouseMove = useCallback(
      (e: React.MouseEvent) => {
        onCursorMove?.(e);
      },
      [onCursorMove]
    );

    // FIXED: Handle text change without triggering re-renders
    const handleTextChange = useCallback(
      (newText: string) => {
        setNoteText(newText);

        // Clear existing timeout
        if (textChangeTimeoutRef.current) {
          clearTimeout(textChangeTimeoutRef.current);
        }

        // Debounce the real-time update to avoid too many socket calls
        textChangeTimeoutRef.current = setTimeout(() => {
          onTextChangeRealtime?.(id, newText);
        }, 100);
      },
      [id, onTextChangeRealtime]
    );

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (textChangeTimeoutRef.current) {
          clearTimeout(textChangeTimeoutRef.current);
        }
      };
    }, []);

    // Modified hover handlers to call both local and parent handlers
    const handleMouseEnter = useCallback(
      (e: React.MouseEvent) => {
        setIsHovered(true);
        onMouseEnter?.(e); // Call parent handler if provided
      },
      [onMouseEnter]
    );

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent) => {
        setIsHovered(false);
        onMouseLeave?.(e); // Call parent handler if provided
      },
      [onMouseLeave]
    );

    const getResponsiveFontSize = () => {
      const width = currentDimensions.width;
      const height = currentDimensions.height;
      const minDimension = Math.min(width, height);
      const fontSize = Math.max(8, Math.min(12, minDimension / 10));
      return `${fontSize}px`;
    };

    const getShapeStyles = () => {
      const baseStyles = {
        width: "100%",
        height: "100%",
        minWidth: 110,
        minHeight: 110,
        backgroundColor: data.color || "#fff59d",
        border: "none",
        borderRadius: 0,
        boxShadow: "0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)",
        "&:hover": {
          boxShadow: "0 8px 20px rgba(0,0,0,0.2), 0 4px 8px rgba(0,0,0,0.15)",
        },
        cursor: isBeingEditedByOther ? "not-allowed" : "default",
        display: "flex",
        flexDirection: "column",
        opacity: isBeingEditedByOther ? 0.7 : 1,
        position: "relative",
        outline: selected || isSelected ? "3px solid #1976d2" : "none",
        outlineOffset: selected || isSelected ? "2px" : "0",
        transform: "rotate(0deg)",
      };

      switch (data.shape) {
        case "circle":
          return {
            ...baseStyles,
            borderRadius: "50%",
            border: selected || isSelected ? "3px solid #1976d2" : "none",
            outline: "none",
          };
        case "diamond":
          return {
            ...baseStyles,
            borderRadius: 0,
            transform: "rotate(45deg)",
            border: selected || isSelected ? "3px solid #1976d2" : "none",
            outline: "none",
            "& .content-container": {
              transform: "rotate(-45deg)",
              width: "65%",
              height: "65%",
              margin: "auto",
            },
          };
        case "ellipse":
          return {
            ...baseStyles,
            borderRadius: "50%",
            border: selected || isSelected ? "3px solid #1976d2" : "none",
            outline: "none",
          };
        default:
          return baseStyles;
      }
    };

    const handleEmojiReactionClick = useCallback(
      (emoji: string, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        onEmojiToggle?.(id, emoji, currentUserId);
      },
      [id, onEmojiToggle, currentUserId]
    );

    const getGroupedReactions = () => {
      const reactions = data.reactions || [];
      const grouped: { [emoji: string]: EmojiReaction[] } = {};

      reactions.forEach((reaction) => {
        if (!grouped[reaction.emoji]) {
          grouped[reaction.emoji] = [];
        }
        grouped[reaction.emoji].push(reaction);
      });

      return grouped;
    };

    const getHandleStyles = (baseStyles: any) => ({
      ...baseStyles,
      opacity: isHovered ? 1 : 0,
      transition: "opacity 0.2s ease-in-out",
      pointerEvents: isHovered ? "auto" : "none",
      zIndex: 1000,
    });

    return (
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ width: "100%", height: "100%" }}
      >
        <NodeResizer
          color="#1976d2"
          isVisible={selected && !isBeingEditedByOther}
          minWidth={110}
          minHeight={110}
          onResize={handleResize}
          onResizeStart={handleResizeStart}
          onResizeEnd={handleResizeEnd}
          keepAspectRatio={true}
        />

        {/* Connection Handles */}
        <Handle
          type="source"
          position={Position.Right}
          id="source"
          style={getHandleStyles({
            background: "#1976d2",
            width: 12,
            height: 12,
            border: "2px solid #fff",
            right: -6,
          })}
        />

        <Handle
          type="target"
          position={Position.Left}
          id="target"
          style={getHandleStyles({
            background: "#dc3545",
            width: 12,
            height: 12,
            border: "2px solid #fff",
            left: -6,
          })}
        />

        <Handle
          type="source"
          position={Position.Top}
          id="source-top"
          style={getHandleStyles({
            background: "#1976d2",
            width: 12,
            height: 12,
            border: "2px solid #fff",
            top: -6,
          })}
        />

        <Handle
          type="target"
          position={Position.Bottom}
          id="target-bottom"
          style={getHandleStyles({
            background: "#dc3545",
            width: 12,
            height: 12,
            border: "2px solid #fff",
            bottom: -6,
          })}
        />

        <Card
          ref={cardRef}
          sx={getShapeStyles()}
          onMouseMove={handleMouseMove}
          elevation={0}
        >
          {/* Editing indicator */}
          {isBeingEditedByOther && editingUser && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                zIndex: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-evenly",
                backgroundColor: editingUser.userColor,
                padding: "2px 4px",
              }}
            >
              <LockIcon sx={{ fontSize: "10px", color: "white" }} />
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.6rem",
                  padding: "1px 2px",
                  color: "white",
                }}
              >
                {editingUser.userName} is editing
              </Typography>
            </Box>
          )}

          <CardContent
            className={data.shape === "diamond" ? "content-container" : ""}
            sx={{
              padding: "8px",
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              "&:last-child": { pb: "8px" },
              ...(data.shape === "diamond" && {
                width: "65%",
                height: "65%",
                margin: "auto",
              }),
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                padding: "4px",
              }}
            >
              {isEditingText && !isBeingEditedByOther ? (
                <TextField
                  ref={textFieldRef}
                  multiline
                  rows={3}
                  value={noteText}
                  onChange={(e) => handleTextChange(e.target.value)}
                  onKeyDown={handleKeyPress}
                  onClick={handleTextFieldClick}
                  onMouseDown={handleTextFieldMouseDown}
                  onFocus={(e) => e.stopPropagation()}
                  onBlur={(e) => {
                    const relatedTarget = e.relatedTarget as Element;
                    if (!cardRef.current?.contains(relatedTarget)) {
                      setTimeout(() => {
                        if (isEditingRef.current) {
                          handleSave();
                        }
                      }, 100);
                    }
                  }}
                  placeholder="Enter text... (Ctrl+Enter to save, Esc to cancel)"
                  variant="standard"
                  size="small"
                  sx={{
                    width: "100%",
                    "& .MuiInputBase-root": {
                      fontSize: getResponsiveFontSize(),
                    },
                    "& .MuiInputBase-input": {
                      textAlign: "center",
                      padding: 0,
                    },
                    "& .MuiInput-underline:before": {
                      borderBottom: "none",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottom: "1px solid #1976d2",
                    },
                    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                      borderBottom: "none",
                    },
                  }}
                  InputProps={{
                    disableUnderline: false,
                  }}
                />
              ) : (
                <Box
                  onClick={handleStartEditingText}
                  onDoubleClick={handleStartEditingText}
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: isBeingEditedByOther ? "not-allowed" : "pointer",
                    fontSize: getResponsiveFontSize(),
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    textAlign: "center",
                    "&:hover": {
                      backgroundColor: isBeingEditedByOther
                        ? "transparent"
                        : "rgba(0,0,0,0.04)",
                    },
                    borderRadius: "2px",
                    padding: "4px",
                    minHeight: "24px",
                  }}
                >
                  {noteText || "Click to add text..."}
                </Box>
              )}
            </Box>
          </CardContent>

          {/* Emoji reactions */}
          {Object.keys(getGroupedReactions()).length > 0 && (
            <Box
              sx={{
                position: "absolute",
                bottom: 6,
                left: 6,
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                maxWidth: currentDimensions.width - 16,
                zIndex: 15,
              }}
            >
              {Object.entries(getGroupedReactions()).map(
                ([emoji, reactions]) => {
                  const userHasReacted = reactions.some(
                    (reaction) => reaction.userId === currentUserId
                  );

                  return (
                    <Chip
                      key={emoji}
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <span style={{ fontSize: "12px" }}>{emoji}</span>
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "10px" }}
                          >
                            {reactions.length}
                          </Typography>
                        </Box>
                      }
                      onClick={(e) => handleEmojiReactionClick(emoji, e)}
                      size="small"
                      variant={userHasReacted ? "filled" : "outlined"}
                      color={userHasReacted ? "primary" : "default"}
                      sx={{
                        height: 22,
                        fontSize: "10px",
                        cursor: "pointer",
                        "& .MuiChip-label": {
                          padding: "0 6px",
                        },
                        "&:hover": {
                          backgroundColor: userHasReacted
                            ? "rgba(25, 118, 210, 0.8)"
                            : "rgba(0, 0, 0, 0.08)",
                        },
                      }}
                    />
                  );
                }
              )}
            </Box>
          )}

          {/* Comment indicator */}
          {data.comments && data.comments.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                bottom: 6,
                right: 6,
                zIndex: 15,
              }}
            >
              <Chip
                icon={<CommentIcon sx={{ fontSize: "12px !important" }} />}
                label={data.comments.length}
                size="small"
                variant="filled"
                color="info"
                sx={{
                  height: 20,
                  fontSize: "10px",
                  cursor: "pointer",
                  "&:hover": {
                    opacity: 0.8,
                  },
                  "& .MuiChip-label": {
                    padding: "0 4px",
                  },
                  "& .MuiChip-icon": {
                    fontSize: "12px",
                  },
                }}
                onClick={handleCommentIndicatorClick}
              />
            </Box>
          )}
        </Card>

        {/* CommentPopover moved here from FourLRetrospectiveInner */}
        {commentPopoverState.anchorEl && (
          <CommentPopover
            anchorEl={commentPopoverState.anchorEl}
            open={Boolean(commentPopoverState.anchorEl)}
            onClose={handleCommentPopoverClose}
            nodeId={id}
            comments={data.comments || []}
            onAddComment={onAddComment}
            onAddCommentReaction={onAddCommentReaction}
            onRemoveCommentReaction={onRemoveCommentReaction}
            currentUserId={currentUserId}
            currentUserName={currentUserName}
            currentUserColor={currentUserColor}
            // className="comment-popover"
          />
        )}
      </div>
    );
  }
);

export default memo(ResizableNoteNode);
